import {
  AggregationConfigs,
  AggregationOptions
} from '../../../components/aggregations'
import { OrderFieldConfig } from '../../../legacy-design-system/order'
import { LibraryAgentLanguage } from '../components/library/library-agent-language'
import { ApplicationLibraryStatus } from '../../../graphql/schema/mutations/update-application-library-status'
import { Vulnerability } from '../../../components/shared'
import { LibraryFilterType, SeverityName } from './libraries-interfaces'
import { ServerEnvironmentEnum } from '../../../components/servers-grid/shared'
import { capitalizeFirstLetter } from './cvssUtils'
import { IntlFormatters } from 'react-intl'
import { AggregationFilter } from '../components/filters/types'
import { BugTracker } from '../../../components/bug-tracker'

export enum ShareByEmailType {
  BY_HASHES = 'BY_HASHES',
  BY_FILTERS = 'BY_FILTERS'
}

export enum CvssScoringType {
  CVSS2 = 'CVSS2',
  CVSS3_1 = 'CVSS3_1'
}

export enum Usage {
  USED = 'Used',
  UNUSED = 'Unused'
}

export type LibraryVersion = string | '?'

type GradeValue = 'A' | 'B' | 'C' | 'D' | 'F' | 'UNKNOWN'

export interface Grade {
  score: number
  value: GradeValue
}

const bucketToID = ({ key: { id } }) => id

const bucketToString = ({ key: { label } }) => label

export const allAggregationConfigs: AggregationConfigs<AllAggregations> = {
  tags: {
    groupName: { id: 'title_tags' }
  },
  languages: {
    groupName: { id: 'title_programmingLanguages' }
  },
  licenses: {
    groupName: { id: 'title_licenses' }
  },
  servers: {
    groupName: { id: 'title_servers' },
    bucketToID,
    bucketToString
  },
  applications: {
    groupName: { id: 'title_applications' },
    bucketToID,
    bucketToString
  },
  grades: {
    groupName: { id: 'title_grades' }
  },
  environments: {
    groupName: { id: 'title_environments' },
    bucketToID,
    bucketToString
  },
  status: {
    groupName: { id: 'title_status' },
    bucketToID,
    bucketToString
  },
  usage: {
    groupName: { id: 'title_loadedAtRuntime' },
    bucketToID,
    bucketToString
  }
}

export const SEVERITY_MESSAGE_ID_MAP = {
  [SeverityName.CRITICAL]: 'title_vulnSeverityTypeCritical',
  [SeverityName.HIGH]: 'title_vulnSeverityTypeHigh',
  [SeverityName.MEDIUM]: 'title_vulnSeverityTypeMedium',
  [SeverityName.LOW]: 'title_vulnSeverityTypeLow',
  [SeverityName.NOTE]: 'title_vulnSeverityTypeNote',
  [SeverityName.NONE]: 'title_libraryStatusNone',
  [SeverityName.OTHER]: 'detail_vulnerabilitySubstatus_other'
}

export const SERVER_ENVIRONMENT_MESSAGE_ID_MAP = {
  [ServerEnvironmentEnum.DEVELOPMENT]: 'title_serverEnvironmentDevelopment',
  [ServerEnvironmentEnum.PRODUCTION]: 'title_serverEnvironmentProduction',
  [ServerEnvironmentEnum.QA]: 'title_serverEnvironmentQualityAssurance'
}

export const FILTER_TYPE_MESSAGE_ID_MAP = {
  [LibraryFilterType.Repos]: 'title_repositories',
  [LibraryFilterType.Apps]: 'title_applications',
  [LibraryFilterType.Languages]: 'title_programmingLanguages',
  [LibraryFilterType.Licenses]: 'title_licenses',
  [LibraryFilterType.Environments]: 'title_environments',
  [LibraryFilterType.Grades]: 'title_grades',
  [LibraryFilterType.Projects]: 'title_projects',
  [LibraryFilterType.Tags]: 'title_tags',
  [LibraryFilterType.Servers]: 'title_servers',
  [LibraryFilterType.Status]: 'title_status',
  [LibraryFilterType.Severity]: 'title_severityLevel',
  [LibraryFilterType.Severities]: 'title_librarySeverity',
  [LibraryFilterType.Usage]: 'title_usage'
}

export const USAGE_MESSAGE_ID_MAP = {
  [Usage.UNUSED]: 'title_libraries_unused',
  [Usage.USED]: 'title_libraries_used'
}

export const LICENSE_MESSAGE_ID_MAP = {
  Unknown: 'title_libraries_unknown'
}

const {
  servers,
  languages,
  tags,
  licenses,
  grades,
  status,
  usage,
  applications,
  environments
} = allAggregationConfigs

export const aggregationConfigs: AggregationConfigs<AggregationCategory> = {
  servers,
  languages,
  tags,
  licenses,
  grades,
  status,
  usage,
  applications,
  environments
}

export const getInitialAggregationSelections =
  (): AggregationOptions<AggregationCategory> => ({
    servers: [],
    languages: [],
    tags: [],
    licenses: [],
    grades: [],
    status: [],
    usage: [],
    applications: [],
    environments: []
  })

export const getFilterLabel = (
  filter: AggregationFilter,
  formatMessage: IntlFormatters['formatMessage']
) => {
  const { label, parentKey, key } = filter
  const messageId =
    FILTER_TYPE_MESSAGE_ID_MAP[key] ||
    (parentKey && SEVERITY_MESSAGE_ID_MAP[key.replace(`${parentKey}.`, '')]) ||
    SERVER_ENVIRONMENT_MESSAGE_ID_MAP[label.toUpperCase()] ||
    USAGE_MESSAGE_ID_MAP[label] ||
    LICENSE_MESSAGE_ID_MAP[label]

  const capitalizedLabel =
    parentKey === 'PROJECTS' ? label : capitalizeFirstLetter(label)

  return messageId ? formatMessage({ id: messageId }) : capitalizedLabel
}
export const LIBRARIES_TABS = { STATIC: 'static', RUNTIME: 'runtime' } as const

export type LibrariesTabType =
  (typeof LIBRARIES_TABS)[keyof typeof LIBRARIES_TABS]

export const GRID_TYPES = {
  SERVER: 'Server',
  ORGANIZATION: 'Organization',
  APPLICATION: 'Application',
  CLI: 'CLI',
  ORGANIZATION_CLI: 'OrganizationCLI'
} as const

export type GridType = (typeof GRID_TYPES)[keyof typeof GRID_TYPES]

export type AggregationCategory =
  | 'applications'
  | 'servers'
  | 'languages'
  | 'tags'
  | 'licenses'
  | 'grades'
  | 'status'
  | 'usage'
  | 'environments'
export interface LibraryUsage {
  total: number
  totalUsed: number
}

export interface RemediationGuidance {
  minUpgrade: {
    version: string
    grade: string
    score: number
    versionsBehind: number
    releaseDate: string
  }
  maxUpgrade: {
    version: string
    grade: string
    score: number
    versionsBehind: number
    releaseDate: string
  }
}
export interface LibraryVersions {
  current: {
    version: LibraryVersion
    releaseDate?: string
  }
  latest: {
    version: LibraryVersion
    releaseDate?: string
  }
}
export interface LibraryPolicies {
  restrictedByOrg: boolean
  invalidVersion: boolean
  versionRestriction?: number
  restrictedLicense?: boolean
}

export interface LibraryApplication {
  id: string
  appId: string
  name: string
  appLibraryStatus?: ApplicationLibraryStatus
}

export interface LibraryServer {
  name: string
  hostname: string
  serverpath: string
  environment: string
  enabled: boolean
  server_id: string
  agent_version: string
}
export interface LibraryClassUsageCount {
  appId: string
  usageCount: number
}
export interface LibraryMergedApplication {
  id: string
  appId: string
  name: string
  appLibraryStatus: ApplicationLibraryStatus
}
export interface Library extends StaticLibrary {
  id: string
  hash: string
  language: LibraryAgentLanguage
  name: string
  grade: Grade
  group?: string
  /** Is the SCA License enabled for this library */
  ossEnabled: boolean
  licenses: string[]
  tags: string[]
  remediationGuidance?: RemediationGuidance
  remediationAdvice?: RemediationAdviceSCA
  bugTrackers: BugTracker[]
  usage: LibraryUsage
  versions: LibraryVersions
  policies: LibraryPolicies
  applications: LibraryApplication[]
  mergedApplications: LibraryMergedApplication[]
  vulnerabilities: Vulnerability[]
  libraryClassUsageCounts: LibraryClassUsageCount[]
  app_language: string
  servers: LibraryServer[]
  custom?: boolean
}

interface RemediationAdviceSCA {
  closestStableVersion: string
  latestStableVersion: string
}

export interface StaticLibrary {
  privateLibrary?: boolean
  severity?: string
}

export type AllAggregations =
  | 'servers'
  | 'languages'
  | 'tags'
  | 'applications'
  | 'licenses'
  | 'grades'
  | 'status'
  | 'usage'
  | 'environments'

export type OrderField = 'NAME' | 'SCORE'

export const orderFieldConfig: OrderFieldConfig<OrderField> = {
  NAME: {
    direction: 'ASC',
    message: { id: 'title_name' }
  },
  SCORE: {
    direction: 'ASC',
    message: { id: 'title_score' }
  }
}
